import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FEATURES } from '@academy/config'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Icon } from '../components/Icon.js'
import { FeatureGate } from '../components/FeatureGate.js'

export const Route = createFileRoute('/_app/tests/$id')({
  component: () => (
    <FeatureGate feature={FEATURES.PERFORMANCE}>
      <TestDetail />
    </FeatureGate>
  ),
})

interface TestDetailResponse {
  test: {
    id: string
    classId: string
    className: string
    name: string
    type: string
    subject: string | null
    maxMarks: number
    testDate: string
  }
  students: {
    studentId: string
    studentName: string
    marks: number | null
    notes: string | null
  }[]
}

function TestDetail() {
  const { id } = Route.useParams()
  const qc = useQueryClient()

  const detail = useQuery({
    queryKey: ['test', id],
    queryFn: async () => {
      const res = await api<TestDetailResponse>(`/api/v1/tests/${id}`)
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [scores, setScores] = useState<Map<string, { marks: string; notes: string }>>(
    new Map(),
  )
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  useEffect(() => {
    if (!detail.data) return
    const next = new Map<string, { marks: string; notes: string }>()
    for (const s of detail.data.students) {
      next.set(s.studentId, {
        marks: s.marks !== null ? String(s.marks) : '',
        notes: s.notes ?? '',
      })
    }
    setScores(next)
  }, [detail.data])

  const setMarks = (id: string, marks: string) => {
    setScores((curr) => {
      const next = new Map(curr)
      const existing = next.get(id) ?? { marks: '', notes: '' }
      next.set(id, { ...existing, marks })
      return next
    })
  }

  const setNotes = (id: string, notes: string) => {
    setScores((curr) => {
      const next = new Map(curr)
      const existing = next.get(id) ?? { marks: '', notes: '' }
      next.set(id, { ...existing, notes })
      return next
    })
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!detail.data) throw new Error('Test not loaded')
      const payload = {
        scores: detail.data.students
          .map((s) => {
            const d = scores.get(s.studentId)
            if (!d || d.marks === '') return null
            const marks = Number(d.marks)
            if (Number.isNaN(marks)) return null
            return {
              studentId: s.studentId,
              marks,
              notes: d.notes || undefined,
            }
          })
          .filter((s): s is NonNullable<typeof s> => s !== null),
      }
      if (payload.scores.length === 0) {
        throw new Error('Enter at least one score before saving')
      }
      const res = await api<{ saved: number }>(`/api/v1/tests/${id}/scores`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['test', id] })
      setSavedAt(new Date().toLocaleTimeString())
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  if (detail.isLoading) {
    return <p className="text-sm text-fg-subtle">Loading…</p>
  }
  if (detail.error || !detail.data) {
    return (
      <p className="text-sm text-danger">
        {detail.error instanceof Error
          ? detail.error.message
          : 'Failed to load test'}
      </p>
    )
  }

  const { test, students } = detail.data

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      <PageHeader
        title={test.name}
        description={
          <>
            <Link to="/tests" className="hover:text-fg">
              ← All tests
            </Link>
            {' · '}
            {test.className}
            {test.subject ? ` · ${test.subject}` : ''}
            {' · max '}
            <strong>{test.maxMarks}</strong>
            {' · '}
            {new Date(test.testDate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </>
        }
      />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-muted text-left">
                <th className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                  Student
                </th>
                <th className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                  Marks (out of {test.maxMarks})
                </th>
                <th className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {students.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-12 text-center text-sm text-fg-subtle"
                  >
                    No students in this class yet.
                  </td>
                </tr>
              ) : (
                students.map((s) => {
                  const d = scores.get(s.studentId) ?? { marks: '', notes: '' }
                  const numeric = d.marks === '' ? null : Number(d.marks)
                  const tooHigh = numeric !== null && numeric > test.maxMarks
                  return (
                    <tr key={s.studentId} className="border-b border-border last:border-b-0">
                      <td className="px-4 py-2 font-medium text-fg">{s.studentName}</td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min="0"
                          max={test.maxMarks}
                          value={d.marks}
                          onChange={(e) => setMarks(s.studentId, e.target.value)}
                          placeholder="—"
                          className={`w-24 rounded-md border bg-surface px-2 py-1 font-mono text-sm focus:outline-none focus:ring-1 ${
                            tooHigh
                              ? 'border-danger ring-danger'
                              : 'border-border focus:border-[var(--color-primary)] focus:ring-[var(--color-primary)]'
                          }`}
                        />
                        {tooHigh ? (
                          <p className="mt-1 text-xs text-danger">
                            Exceeds max ({test.maxMarks})
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-2">
                        <input
                          value={d.notes}
                          onChange={(e) => setNotes(s.studentId, e.target.value)}
                          placeholder="—"
                          className="w-full bg-transparent border-0 px-0 py-1 text-sm text-fg placeholder-fg-faint focus:outline-none focus:ring-0"
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="fixed bottom-0 left-0 right-0 md:static z-40 border-t border-border bg-surface md:bg-transparent md:border-0 px-4 py-3 md:px-0 md:py-0 shadow-md md:shadow-none">
        <div className="max-w-[1280px] mx-auto flex items-center justify-between gap-3">
          <p className="text-xs text-fg-subtle min-w-0 flex-1">
            {savedAt ? (
              <span className="inline-flex items-center gap-1 text-success">
                <Icon name="check" size={12} />
                Saved at {savedAt}
              </span>
            ) : (
              'Saving fires marks-updated notifications.'
            )}
            {error ? <span className="ml-2 text-danger">{error}</span> : null}
          </p>
          <Button
            onClick={() => save.mutate()}
            disabled={students.length === 0 || save.isPending}
            loading={save.isPending}
            size="lg"
          >
            Save scores
          </Button>
        </div>
      </div>
    </div>
  )
}
